import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Check, ChevronDown, ChevronRight, Loader2, Palette, Pencil, Settings2 } from 'lucide-react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import SpinnerNew from '../../../components/main/SpinnerNew/SpinnerNew';
import EditTagDialog from '../../../components/dialogs/EditTagDialog/EditTagDialog';
import { getReadableTextColour } from '../../../contexts/TagTypeColourContext';

const DEFAULT_LIGHT_MODE_COLOUR = '#E5E7EB';
const DEFAULT_DARK_MODE_COLOUR = '#374151';
const HEX_COLOUR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
const UNCATEGORISED = 'Uncategorised';

type BrandColourPreset = {
    name: string;
    light: string;
    dark: string;
};

const BRAND_COLOUR_PRESET_GROUPS: { name: string; description: string; presets: BrandColourPreset[] }[] = [
    {
        name: 'Primary and secondary',
        description: 'Use these first for general tag categories.',
        presets: [
            { name: 'Aurecon Green', light: '#89C925', dark: '#89C925' },
            { name: 'Aurecon Grey', light: '#EAECEC', dark: '#373A36' },
            { name: 'Grey 2', light: '#EAECEC', dark: '#4E5859' },
            { name: 'Grey 3', light: '#F2F4F4', dark: '#8E9C9C' },
            { name: 'Grey 4', light: '#F7F8F8', dark: '#BBC6C3' },
        ],
    },
    {
        name: 'Tertiary highlights',
        description: 'Reserve these for categories that need additional distinction.',
        presets: [
            { name: 'Dark Green', light: '#ECF2EB', dark: '#577740' },
            { name: 'Dark Blue', light: '#E9EBED', dark: '#3C556A' },
            { name: 'Teal', light: '#E5F7F7', dark: '#3CB2B1' },
            { name: 'Yellow', light: '#FFF8E9', dark: '#FCD672' },
            { name: 'Melon', light: '#FDF1ED', dark: '#EFA693' },
        ],
    },
];

const GET_TAG_TYPE_SETTINGS = gql`
    query GetTagTypeSettings {
        TagTypeSettings {
            id
            name
            description
            requiredForContentRequests
            lightModeColour
            darkModeColour
        }
    }
`;

const SET_TAG_TYPE_REQUIRED = gql`
    mutation SetTagTypeRequiredForContentRequests($name: String!, $required: Boolean!) {
        SetTagTypeRequiredForContentRequests(name: $name, required: $required)
    }
`;

const SET_TAG_TYPE_COLOURS = gql`
    mutation SetTagTypeColours(
        $name: String!
        $lightModeColour: String!
        $darkModeColour: String!
    ) {
        SetTagTypeColours(
            name: $name
            lightModeColour: $lightModeColour
            darkModeColour: $darkModeColour
        )
    }
`;

const UPDATE_TAG_TYPE = gql`
    mutation UpdateTagTypeDetails($currentName: String!, $name: String!, $description: String) {
        UpdateTagType(currentName: $currentName, name: $name, description: $description)
    }
`;

export type TagSettingItem = {
    id?: string | null;
    name?: string | null;
    description?: string | null;
    type?: string | null;
    isArchived?: boolean | null;
};

type TagTypeSetting = {
    id?: string | null;
    name?: string | null;
    description?: string | null;
    requiredForContentRequests?: boolean | null;
    lightModeColour?: string | null;
    darkModeColour?: string | null;
};

type TagTypeSettingsProps = {
    tags?: TagSettingItem[];
    selectedIds?: string[];
    onSelected?: (ids: string[]) => void;
    onRefresh?: () => Promise<void> | void;
    refreshVersion?: number;
};

const ColourField = ({
    label,
    value,
    fallback,
    onChange,
}: {
    label: string;
    value: string;
    fallback: string;
    onChange: (value: string) => void;
}) => {
    const isValid = HEX_COLOUR_PATTERN.test(value);
    const previewColour = isValid ? value : fallback;
    return (
        <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium" style={{ color: 'var(--text-secondary)' }}>{label}</span>
            <span className="flex items-center gap-1.5">
                <input
                    type="color"
                    aria-label={`${label} picker`}
                    value={previewColour}
                    onChange={event => onChange(event.target.value.toUpperCase())}
                    style={{
                        width: 30,
                        height: 28,
                        padding: 2,
                        border: '1px solid var(--border-default)',
                        borderRadius: 6,
                        background: 'var(--bg-card)',
                        cursor: 'pointer',
                    }}
                />
                <input
                    className="input-base"
                    aria-label={label}
                    value={value}
                    maxLength={7}
                    onChange={event => onChange(event.target.value)}
                    style={{ width: 84, height: 28, fontSize: 11, fontFamily: 'monospace' }}
                />
                <span
                    aria-label={`${label} text preview`}
                    className="rounded px-1.5 py-1 text-[10px] font-semibold"
                    style={{
                        background: previewColour,
                        color: getReadableTextColour(previewColour),
                    }}
                >
                    Aa
                </span>
            </span>
            {!isValid && <span className="text-[10px]" style={{ color: 'var(--danger)' }}>Use #RRGGBB</span>}
        </label>
    );
};

const BrandPalettePicker = ({
    lightColour,
    darkColour,
    onSelect,
}: {
    lightColour: string;
    darkColour: string;
    onSelect: (preset: BrandColourPreset) => void;
}) => (
    <section className="mt-5 rounded-lg border p-4" style={{ borderColor: 'var(--border-default)' }}>
        <div className="flex items-start gap-2">
            <Palette size={16} style={{ color: 'var(--text-secondary)', marginTop: 1, flexShrink: 0 }} />
            <div>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)', margin: 0 }}>
                    Brand presets <span className="font-normal" style={{ color: 'var(--text-tertiary)' }}>(optional)</span>
                </h3>
                <div className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                    A preset sets both modes. You can still adjust either hex value afterwards.
                </div>
            </div>
        </div>
        <div className="flex flex-col gap-4 mt-4">
            {BRAND_COLOUR_PRESET_GROUPS.map(group => (
                <div key={group.name}>
                    <div className="mb-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                            {group.name}
                        </div>
                        <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{group.description}</div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {group.presets.map(preset => {
                            const isSelected = lightColour.toUpperCase() === preset.light
                                && darkColour.toUpperCase() === preset.dark;
                            return (
                                <button
                                    key={preset.name}
                                    type="button"
                                    aria-label={`Use ${preset.name} brand preset`}
                                    aria-pressed={isSelected}
                                    title={`Light ${preset.light} / Dark ${preset.dark}`}
                                    onClick={() => onSelect(preset)}
                                    className="flex items-center gap-2 rounded-md border px-2.5 py-2 text-left transition-colors"
                                    style={{
                                        borderColor: isSelected ? 'var(--accent)' : 'var(--border-default)',
                                        background: isSelected ? 'var(--accent-soft)' : 'var(--bg-card)',
                                        color: 'var(--text-primary)',
                                    }}
                                >
                                    <span
                                        className="flex h-7 w-9 overflow-hidden rounded border flex-shrink-0"
                                        style={{ borderColor: 'var(--border-default)' }}
                                        aria-hidden="true"
                                    >
                                        <span className="h-full flex-1" style={{ background: preset.light }} />
                                        <span className="h-full flex-1" style={{ background: preset.dark }} />
                                    </span>
                                    <span className="min-w-0 flex-1 truncate text-[11px] font-medium">{preset.name}</span>
                                    {isSelected && <Check size={13} style={{ flexShrink: 0 }} />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    </section>
);

const TagTypeSettingsDialog = ({
    setting,
    isSaving,
    error,
    onClose,
    onSave,
}: {
    setting: TagTypeSetting;
    isSaving: boolean;
    error: string | null;
    onClose: () => void;
    onSave: (setting: TagTypeSetting, name: string, description: string, light: string, dark: string, required: boolean) => void;
}) => {
    const savedName = setting.name || '';
    const savedDescription = setting.description || '';
    const savedLightColour = setting.lightModeColour || DEFAULT_LIGHT_MODE_COLOUR;
    const savedDarkColour = setting.darkModeColour || DEFAULT_DARK_MODE_COLOUR;
    const [name, setName] = useState(savedName);
    const [description, setDescription] = useState(savedDescription);
    const [lightColour, setLightColour] = useState(savedLightColour);
    const [darkColour, setDarkColour] = useState(savedDarkColour);
    const [required, setRequired] = useState(!!setting.requiredForContentRequests);

    useEffect(() => {
        setName(savedName);
        setDescription(savedDescription);
        setLightColour(savedLightColour);
        setDarkColour(savedDarkColour);
        setRequired(!!setting.requiredForContentRequests);
    }, [savedDarkColour, savedDescription, savedLightColour, savedName, setting.requiredForContentRequests]);

    const coloursAreValid = HEX_COLOUR_PATTERN.test(lightColour) && HEX_COLOUR_PATTERN.test(darkColour);
    const isDirty =
        name.trim() !== savedName
        || description.trim() !== savedDescription
        || lightColour.toUpperCase() !== savedLightColour.toUpperCase()
        || darkColour.toUpperCase() !== savedDarkColour.toUpperCase()
        || required !== !!setting.requiredForContentRequests;

    return (
        <DialogBase
            title={`${setting.name} tag type`}
            type="Custom"
            visible
            width="640px"
            onClose={onClose}
            validationError={error || undefined}
            actions={(
                <>
                    <button className="btn btn-secondary" onClick={onClose} disabled={isSaving}>Cancel</button>
                    <button
                        className="btn btn-primary flex items-center gap-2"
                        disabled={!isDirty || !name.trim() || !description.trim() || !coloursAreValid || isSaving}
                        onClick={() => onSave(
                            setting,
                            name.trim(),
                            description.trim(),
                            lightColour.toUpperCase(),
                            darkColour.toUpperCase(),
                            required,
                        )}
                    >
                        {isSaving && <Loader2 size={14} className="animate-spin" />}
                        Save settings
                    </button>
                </>
            )}
        >
            <p className="text-sm mb-5" style={{ color: 'var(--text-tertiary)' }}>
                These colours are used for {setting.name} badges throughout the website and Content Browser.
            </p>
            <div className="grid grid-cols-1 gap-4 mb-5">
                <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Name</span>
                    <input className="input-base" aria-label="Tag type name" value={name} onChange={event => setName(event.target.value)} />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Description</span>
                    <textarea className="input-base" aria-label="Tag type description" value={description} onChange={event => setDescription(event.target.value)} rows={3} />
                </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-lg border p-4" style={{ borderColor: 'var(--border-default)', background: 'var(--bg-subtle)' }}>
                    <ColourField label="Light mode" value={lightColour} fallback={DEFAULT_LIGHT_MODE_COLOUR} onChange={setLightColour} />
                </div>
                <div className="rounded-lg border p-4" style={{ borderColor: 'var(--border-default)', background: 'var(--bg-subtle)' }}>
                    <ColourField label="Dark mode" value={darkColour} fallback={DEFAULT_DARK_MODE_COLOUR} onChange={setDarkColour} />
                </div>
            </div>
            <BrandPalettePicker
                lightColour={lightColour}
                darkColour={darkColour}
                onSelect={preset => {
                    setLightColour(preset.light);
                    setDarkColour(preset.dark);
                }}
            />
            <label className="flex items-start gap-3 mt-5 rounded-lg border p-4" style={{ borderColor: 'var(--border-default)' }}>
                <input
                    type="checkbox"
                    checked={required}
                    disabled={isSaving}
                    onChange={event => setRequired(event.target.checked)}
                    className="mt-0.5"
                />
                <span>
                    <span className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Required for content requests</span>
                    <span className="block text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                        At least one tag of this type must be selected before a request can be submitted.
                    </span>
                </span>
            </label>
        </DialogBase>
    );
};

const ColourSummary = ({ label, colour, fallback }: { label: string; colour?: string | null; fallback: string }) => {
    const background = colour && HEX_COLOUR_PATTERN.test(colour) ? colour : fallback;
    return (
        <span
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[10px] font-semibold border"
            style={{
                background,
                color: getReadableTextColour(background),
                borderColor: 'var(--border-default)',
            }}
            title={`${label}: ${background}`}
            aria-label={`${label}: ${background}`}
        >
            {label.charAt(0)}
        </span>
    );
};

const TagTypeSettings = ({
    tags = [],
    selectedIds = [],
    onSelected,
    onRefresh,
    refreshVersion = 0,
}: TagTypeSettingsProps) => {
    const { data, loading, error, refetch } = useQuery(GET_TAG_TYPE_SETTINGS, {
        fetchPolicy: 'network-only',
    });
    const [setRequired] = useMutation(SET_TAG_TYPE_REQUIRED);
    const [setColours] = useMutation(SET_TAG_TYPE_COLOURS);
    const [updateTagType] = useMutation(UPDATE_TAG_TYPE);
    const [savingName, setSavingName] = useState<string | null>(null);
    const [mutationError, setMutationError] = useState<string | null>(null);
    const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
    const [editingSetting, setEditingSetting] = useState<TagTypeSetting | null>(null);
    const [editingTag, setEditingTag] = useState<TagSettingItem | null>(null);
    const settings = (data?.TagTypeSettings ?? []) as TagTypeSetting[];

    useEffect(() => {
        if (refreshVersion > 0) refetch();
    }, [refreshVersion, refetch]);

    const groups = useMemo(() => {
        const settingsByName = new Map(
            settings
                .filter(setting => setting.name)
                .map(setting => [setting.name!.toLowerCase(), setting])
        );
        const names = new Set(settings.filter(setting => setting.name).map(setting => setting.name!));
        tags.forEach(tag => names.add(tag.type || UNCATEGORISED));

        return Array.from(names)
            .sort((a, b) => a.localeCompare(b))
            .map(name => ({
                name,
                setting: settingsByName.get(name.toLowerCase()) || { name },
                tags: tags
                    .filter(tag => (tag.type || UNCATEGORISED).toLowerCase() === name.toLowerCase())
                    .sort((a, b) => {
                        if (!!a.isArchived !== !!b.isArchived) return a.isArchived ? 1 : -1;
                        return (a.name || '').localeCompare(b.name || '');
                    }),
            }));
    }, [settings, tags]);

    const saveSettings = async (
        setting: TagTypeSetting,
        name: string,
        description: string,
        lightModeColour: string,
        darkModeColour: string,
        required: boolean,
    ) => {
        if (!setting.name) return;
        setSavingName(setting.name);
        setMutationError(null);
        try {
            await updateTagType({ variables: { currentName: setting.name, name, description } });
            const mutations: Promise<unknown>[] = [
                setColours({ variables: { name, lightModeColour, darkModeColour } }),
            ];
            if (required !== !!setting.requiredForContentRequests) {
                mutations.push(setRequired({ variables: { name, required } }));
            }
            await Promise.all(mutations);
            await refetch();
            await onRefresh?.();
            setEditingSetting(null);
        } catch (err: any) {
            setMutationError(err?.message || 'Unable to update the tag type settings.');
        } finally {
            setSavingName(null);
        }
    };

    const updateGroupSelection = (groupTags: TagSettingItem[], selected: boolean) => {
        if (!onSelected) return;
        const groupIds = groupTags.map(tag => tag.id).filter((id): id is string => !!id);
        const next = new Set(selectedIds);
        groupIds.forEach(id => selected ? next.add(id) : next.delete(id));
        onSelected(Array.from(next));
    };

    const updateRowSelection = (id: string, selected: boolean) => {
        if (!onSelected) return;
        const next = new Set(selectedIds);
        selected ? next.add(id) : next.delete(id);
        onSelected(Array.from(next));
    };

    if (loading) return <SpinnerNew size="medium" label="Loading tag settings..." />;
    if (error) return <div className="p-6 text-sm" style={{ color: 'var(--danger)' }}>Unable to load tag settings.</div>;

    return (
        <div className="p-4 overflow-auto">
            {mutationError && !editingSetting && (
                <div className="rounded-md px-3 py-2 text-sm mb-3" style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }}>
                    {mutationError}
                </div>
            )}
            <div className="flex flex-col gap-3">
                {groups.map(group => {
                    const isCollapsed = !expandedTypes.has(group.name);
                    const activeCount = group.tags.filter(tag => !tag.isArchived).length;
                    const archivedCount = group.tags.length - activeCount;
                    const groupIds = group.tags.map(tag => tag.id).filter((id): id is string => !!id);
                    const allSelected = groupIds.length > 0 && groupIds.every(id => selectedIds.includes(id));
                    return (
                        <section key={group.name} className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-default)' }}>
                            <div className="flex items-center gap-3 px-4 py-3" style={{ background: 'var(--bg-subtle)' }}>
                                <button
                                    className="flex flex-1 min-w-0 items-center gap-2 text-left"
                                    onClick={() => setExpandedTypes(previous => {
                                        const next = new Set(previous);
                                        next.has(group.name) ? next.delete(group.name) : next.add(group.name);
                                        return next;
                                    })}
                                    aria-expanded={!isCollapsed}
                                >
                                    {isCollapsed ? <ChevronRight size={17} /> : <ChevronDown size={17} />}
                                    <span className="truncate text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{group.name}</span>
                                </button>
                                <span className="badge-default whitespace-nowrap text-[11px]">{activeCount} active</span>
                                {archivedCount > 0 && (
                                    <span className="badge-default whitespace-nowrap text-[11px]" style={{ opacity: 0.65 }}>
                                        {archivedCount} archived
                                    </span>
                                )}
                                {group.setting.requiredForContentRequests && (
                                    <span className="badge-accent inline-flex items-center gap-1 whitespace-nowrap text-[11px]">
                                        <Check size={11} /> Required
                                    </span>
                                )}
                                <div className="flex items-center gap-1" title="Badge colour previews">
                                    <ColourSummary label="Light" colour={group.setting.lightModeColour} fallback={DEFAULT_LIGHT_MODE_COLOUR} />
                                    <ColourSummary label="Dark" colour={group.setting.darkModeColour} fallback={DEFAULT_DARK_MODE_COLOUR} />
                                </div>
                                <button
                                    className="btn btn-ghost p-1.5"
                                    aria-label={`Configure ${group.name}`}
                                    title={`Configure ${group.name}`}
                                    onClick={() => {
                                        setMutationError(null);
                                        setEditingSetting(group.setting);
                                    }}
                                >
                                    <Settings2 size={16} />
                                </button>
                            </div>

                            {!isCollapsed && (
                                group.tags.length ? (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-t border-b" style={{ borderColor: 'var(--border-default)', color: 'var(--text-tertiary)' }}>
                                                    <th className="w-10 px-4 py-2 text-left">
                                                        <input
                                                            type="checkbox"
                                                            aria-label={`Select all ${group.name} tags`}
                                                            checked={allSelected}
                                                            onChange={event => updateGroupSelection(group.tags, event.target.checked)}
                                                        />
                                                    </th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium">Name</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium">Description</th>
                                                    <th className="w-28 px-3 py-2 text-left text-xs font-medium">Archived</th>
                                                    <th className="w-12 px-3 py-2" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {group.tags.map(tag => (
                                                    <tr
                                                        key={tag.id || `${group.name}-${tag.name}`}
                                                        className="border-b last:border-b-0"
                                                        style={{
                                                            borderColor: 'var(--border-subtle)',
                                                            background: tag.isArchived ? 'var(--bg-subtle)' : undefined,
                                                            color: tag.isArchived ? 'var(--text-tertiary)' : 'var(--text-primary)',
                                                            opacity: tag.isArchived ? 0.62 : 1,
                                                        }}
                                                    >
                                                        <td className="px-4 py-2.5">
                                                            {tag.id && (
                                                                <input
                                                                    type="checkbox"
                                                                    aria-label={`Select ${tag.name}`}
                                                                    checked={selectedIds.includes(tag.id)}
                                                                    onChange={event => updateRowSelection(tag.id!, event.target.checked)}
                                                                />
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2.5 font-medium">{tag.name}</td>
                                                        <td className="px-3 py-2.5">{tag.description || '—'}</td>
                                                        <td className="px-3 py-2.5">{tag.isArchived ? 'Yes' : 'No'}</td>
                                                        <td className="px-3 py-2.5">
                                                            <button
                                                                className="btn btn-ghost p-1.5"
                                                                aria-label={`Edit ${tag.name}`}
                                                                title={`Edit ${tag.name}`}
                                                                onClick={() => setEditingTag(tag)}
                                                            >
                                                                <Pencil size={14} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="px-4 py-5 text-sm border-t" style={{ borderColor: 'var(--border-default)', color: 'var(--text-tertiary)' }}>
                                        No tags have been created for this type.
                                    </p>
                                )
                            )}
                        </section>
                    );
                })}
                {groups.length === 0 && (
                    <p className="p-6 text-sm text-center" style={{ color: 'var(--text-tertiary)' }}>
                        Create a tag to create its tag type.
                    </p>
                )}
            </div>
            {editingSetting && (
                <TagTypeSettingsDialog
                    key={editingSetting.name}
                    setting={editingSetting}
                    isSaving={savingName === editingSetting.name}
                    error={mutationError}
                    onClose={() => {
                        if (!savingName) {
                            setEditingSetting(null);
                            setMutationError(null);
                        }
                    }}
                    onSave={saveSettings}
                />
            )}
            {editingTag && (
                <EditTagDialog
                    tag={editingTag}
                    onClose={async saved => {
                        setEditingTag(null);
                        if (saved) await onRefresh?.();
                    }}
                />
            )}
        </div>
    );
};

export default TagTypeSettings;
